import { use, useLayoutEffect } from 'react'

import { ShellTitleContext } from '#providers/ShellTitleProvider'
import type { IBreadcrumb } from '#providers/ShellTitleProvider/interfaces'

interface IShellTitleState {
  title?: string
  subtitle?: string
  breadcrumbs: IBreadcrumb[]
}

const NO_BREADCRUMBS: IBreadcrumb[] = []

/**
 * Shows `title` and `subtitle` in the shell header (AppHeader) while the calling component is
 * mounted. Outside a ShellTitleProvider (unit tests, Storybook) it does nothing.
 */
export function useShellTitle(title: string | undefined, subtitle?: string): void {
  const context = use(ShellTitleContext)
  const pathname = context?.pathname
  const publish = context?.publish
  const retract = context?.retract

  // Before paint, not in a passive effect. Otherwise the shell keeps the route
  // fallback (the parent title) for a frame after the page is already visible.
  useLayoutEffect((): (() => void) | undefined => {
    if (!title || pathname === undefined || !publish || !retract) return undefined

    const entry = { title, subtitle, pathname }
    publish(entry)

    return (): void => retract(entry)
  }, [title, subtitle, pathname, publish, retract])
}

/** What the shell header shows: the page's title, else the route's, plus the crumbs above the page. */
export function useShellTitleState(): IShellTitleState {
  const context = use(ShellTitleContext)

  return {
    title: context?.title,
    subtitle: context?.subtitle,
    breadcrumbs: context?.breadcrumbs ?? NO_BREADCRUMBS,
  }
}
